import { AlertBanner, BDDialog, Flex } from '@brightdrop/bd-ui';
import { Typography } from '@mui/material';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

const TRANSLATION_KEYS = {
  title: 'integrations:account.manage.unlinkConfirmTitle',
  confirm: 'integrations:account.manage.unlink',
  error: 'integrations:account.manage.unlinkError',
  close: 'common:close',
} as const;

type UnlinkConfirmDialogProps = {
  isOpen: boolean;
  partnerName: string;
  bodyKey: string;
  isUnlinking: boolean;
  hasError: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const UnlinkConfirmDialog = ({
  isOpen,
  partnerName,
  bodyKey,
  isUnlinking,
  hasError,
  onConfirm,
  onClose,
}: UnlinkConfirmDialogProps): JSX.Element => {
  const { t } = useTranslation(['integrations', 'common']);

  // BDDialog takes a component type; memoized so the body is not remounted on every render.
  const Body = useMemo(
    () =>
      function UnlinkConfirmBody() {
        return (
          <Flex col gap={16}>
            {hasError && (
              <AlertBanner
                variant="error"
                open
                title={translateText(TRANSLATION_KEYS.error)}
                testId="account-integrations-unlink-error-banner"
              />
            )}
            <Typography
              variant="body_regular"
              component="div"
              data-testid="account-integrations-unlink-confirm-body"
            >
              <Trans t={t} i18nKey={bodyKey} />
            </Typography>
          </Flex>
        );
      },
    [bodyKey, hasError, t]
  );

  const title = translateText(TRANSLATION_KEYS.title, { partner: partnerName });

  return (
    <BDDialog
      openModal={isOpen}
      // `disableClose` only hides the close button; backdrop and Escape still call this.
      onCloseModal={() => {
        if (!isUnlinking) onClose();
      }}
      dialogSize="sm"
      title={title}
      component={Body}
      disableClose={isUnlinking}
      labels={{ close: translateText(TRANSLATION_KEYS.close), aria: title }}
      buttonsConfig={[
        {
          id: 'account-integrations-unlink-confirm',
          displayLabel: translateText(TRANSLATION_KEYS.confirm),
          accessibleLabel: translateText(TRANSLATION_KEYS.confirm),
          destructive: true,
          disabled: isUnlinking,
          onClick: onConfirm,
        },
      ]}
    />
  );
};

export default UnlinkConfirmDialog;
